import { z } from "zod";

const version = z.number().int().min(0).max(2147483647);
const revision = z.number().int().min(1).max(2147483647);
export const configurationIdSchema = z.uuid();
export const configurationAppIdSchema = z.string().trim().min(1).max(128);
const timestamp = z.string().min(1);
// Public support links use a DNS host and standard HTTPS port. Keep the matching
// SQL validator in 20260914150000_configuration_foundation.sql and
// 20260916280000_app_bilingual_copy.sql in sync.
// The control-character range intentionally rejects ASCII controls in URLs.
// eslint-disable-next-line no-control-regex
const supportUrlPattern = /^https:\/\/([A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}([/?#][^\s\\\u0000-\u001f\u007f]*)?$/;

// Only public, typed data is accepted. Credentials belong to dedicated providers.
export const applicationProfileValuesSchema = z.object({
  displayName: z.string().trim().min(2).max(64),
  displayNameUg: z.string().trim().min(2).max(64),
  defaultLocale: z.enum(["zh-CN", "ug-CN", "en-US"]),
  timezone: z.enum(["Asia/Shanghai", "UTC", "America/Los_Angeles"]),
  supportEmail: z.union([z.literal(""), z.email().max(254)]),
  supportUrl: z.union([z.literal(""), z.string().max(500).regex(supportUrlPattern, "请输入 HTTPS 网站地址，不含端口或登录凭据")]),
}).strict();
export type ApplicationProfileValues = z.infer<typeof applicationProfileValuesSchema>;
export const defaultApplicationProfileValues: ApplicationProfileValues = {
  displayName: "", displayNameUg: "", defaultLocale: "zh-CN", timezone: "Asia/Shanghai", supportEmail: "", supportUrl: "",
};

export const configurationRevisionSchema = z.object({
  revision, values: applicationProfileValuesSchema, publishedAt: timestamp, publishedBy: z.string(),
  action: z.enum(["publish", "rollback"]), sourceRevision: revision.nullable(),
});
export type ConfigurationRevision = z.infer<typeof configurationRevisionSchema>;
export const configurationProfileSchema = z.object({
  id: configurationIdSchema, module: z.literal("application_public"), schemaVersion: z.literal(1),
  environment: z.enum(["production", "development"]),
  name: z.string().trim().min(2).max(64), description: z.string().max(200),
  status: z.enum(["active", "disabled"]), version, draft: applicationProfileValuesSchema,
  published: configurationRevisionSchema.nullable(), boundAppCount: z.number().int().min(0),
  createdAt: timestamp, updatedAt: timestamp, updatedBy: z.string(),
});
export type ConfigurationProfile = z.infer<typeof configurationProfileSchema>;
export const configurationListQuerySchema = z.object({
  keyword: z.string().trim().max(80).default(""),
  status: z.enum(["all", "active", "disabled"]).default("all"),
  publishedOnly: z.enum(["true", "false"]).default("false"),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export type ConfigurationListQuery = z.infer<typeof configurationListQuerySchema>;
export const configurationPageSchema = z.object({ items: z.array(configurationProfileSchema), total: z.number().int().min(0) });
export type ConfigurationPage = z.infer<typeof configurationPageSchema>;
export const configurationPaginationSchema = z.object({
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export type ConfigurationPagination = z.infer<typeof configurationPaginationSchema>;
export const configurationRevisionsPageSchema = z.object({ items: z.array(configurationRevisionSchema), total: z.number().int().min(0) });
export type ConfigurationRevisionsPage = z.infer<typeof configurationRevisionsPageSchema>;

const draftFields = { name: z.string().trim().min(2).max(64), description: z.string().trim().max(200), values: applicationProfileValuesSchema };
export const configurationCreateSchema = z.object({ ...draftFields, mutationId: z.uuid() }).strict();
export type ConfigurationCreateInput = z.infer<typeof configurationCreateSchema>;
export const configurationUpdateSchema = z.object({ ...draftFields, expectedVersion: version }).strict();
export type ConfigurationUpdateInput = z.infer<typeof configurationUpdateSchema>;
export const configurationPublishSchema = z.object({ expectedVersion: version, mutationId: z.uuid() }).strict();
export type ConfigurationPublishInput = z.infer<typeof configurationPublishSchema>;
export const configurationRollbackSchema = configurationPublishSchema.extend({ targetRevision: revision }).strict();
export type ConfigurationRollbackInput = z.infer<typeof configurationRollbackSchema>;
export const configurationStatusSchema = z.object({ status: z.enum(["active", "disabled"]), expectedVersion: version }).strict();
export type ConfigurationStatusInput = z.infer<typeof configurationStatusSchema>;

export const configurationBindingSchema = z.object({
  appId: configurationAppIdSchema, version, mode: z.literal("fixed"),
  profileId: configurationIdSchema.nullable(), profileName: z.string().nullable(), revision: revision.nullable(),
  updatedAt: timestamp.nullable(), updatedBy: z.string().nullable(),
});
export type ConfigurationBinding = z.infer<typeof configurationBindingSchema>;
export const configurationBindingUpdateSchema = z.object({
  profileId: configurationIdSchema.nullable(), revision: revision.nullable(), expectedVersion: version,
}).strict().refine((value) => (value.profileId === null) === (value.revision === null), "配置与发布版本必须同时选择或同时清空");
export type ConfigurationBindingUpdateInput = z.infer<typeof configurationBindingUpdateSchema>;
export const configurationBoundAppsPageSchema = z.object({
  items: z.array(configurationBindingSchema.extend({ appName: z.string(), appCode: z.string(), appStatus: z.enum(["active", "disabled"]) })),
  total: z.number().int().min(0),
});
export type ConfigurationBoundAppsPage = z.infer<typeof configurationBoundAppsPageSchema>;
export const configurationAuditPageSchema = z.object({ items: z.array(z.object({
  id: z.string(), actorId: z.string(), action: z.enum(["create", "update", "publish", "rollback", "enable", "disable", "bind", "unbind"]),
  profileId: configurationIdSchema, appId: configurationAppIdSchema.nullable(),
  version, revision: revision.nullable(), requestId: z.string().nullable(), createdAt: timestamp,
})), total: z.number().int().min(0) });
export type ConfigurationAuditPage = z.infer<typeof configurationAuditPageSchema>;

// Independent from legacy /open/config. No draft, administrative metadata or keys.
export const publishedApplicationProfileSchema = z.object({
  schemaVersion: z.literal(1),
  profile: z.object({ profileId: configurationIdSchema, revision, values: applicationProfileValuesSchema }).strict().nullable(),
}).strict();
export type PublishedApplicationProfile = z.infer<typeof publishedApplicationProfileSchema>;
